import { Organization } from "@/types/user";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Building2, Mail, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { organizationsService } from "@/services/organizationsService";

type OrganizationSelectionStepProps = {
  selectedOrganizationId: string;
  setSelectedOrganizationId: (id: string) => void;
  selectedOrganizationName: string;
  setSelectedOrganizationName: (name: string) => void;
  userEmail?: string;
  currentUserId?: string;
};

const OrganizationSelectionStep = ({
  selectedOrganizationId,
  setSelectedOrganizationId,
  selectedOrganizationName,
  setSelectedOrganizationName,
  userEmail = "",
  currentUserId,
}: OrganizationSelectionStepProps) => {
  const [inviteCode, setInviteCode] = useState("");
  const [newOrgName, setNewOrgName] = useState("");
  const [domainOrganizations, setDomainOrganizations] = useState<Organization[]>([]);
  const [selectionMode, setSelectionMode] = useState<"domain" | "invite" | "create">("domain");
  const [isLoading, setIsLoading] = useState(false);

  // Extract domain from email
  const emailDomain = userEmail ? userEmail.split("@")[1] : "";

  useEffect(() => {
    const fetchDomainOrganizations = async () => {
      // Only fetch if email domain is available
      if (!emailDomain) {
        setDomainOrganizations([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Use new endpoint to get organizations by domain (returns array)
        const orgsData = await organizationsService.getOrganizationByDomain(emailDomain);
        
        // API returns array of organizations (can be empty, single, or multiple)
        const organizations = Array.isArray(orgsData) ? orgsData.map((org: any) => ({
          id: org.id,
          name: org.name,
          slug: org.slug,
          domain: org.domain,
          description: org.description,
        })) : [];

        setDomainOrganizations(organizations);

        // Auto-select if only one organization found
        if (organizations.length === 1) {
          setSelectedOrganizationId(organizations[0].id);
          setSelectedOrganizationName(organizations[0].name);
        }
      } catch (error: any) {
        // Handle 404 as "no organization found" (not an error)
        if (error?.response?.status === 404) {
          setDomainOrganizations([]);
        } else {
          console.error("Error fetching domain organizations:", error);
          setDomainOrganizations([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Always fetch when component mounts or emailDomain changes
    fetchDomainOrganizations();
  }, [emailDomain, setSelectedOrganizationId, setSelectedOrganizationName]);

  const handleInviteCodeValidation = async () => {
    if (!inviteCode.trim()) {
      toast.error("Please enter an invite code");
      return;
    }

    setIsLoading(true);
    try {
      // Check if invitation is valid
      // Note: apiService.get returns the data directly (unwrapped from response.data.data)
      const inviteData = await organizationsService.checkInvitation(inviteCode.trim());

      if (!inviteData) {
        toast.error("Invalid invite code");
        return;
      }

      if (!inviteData.valid) {
        toast.error("This invitation code is invalid, expired, or has already been used");
        return;
      }

      // Valid invitation - store the organization details
      setSelectedOrganizationId(inviteData.organizationId);
      setSelectedOrganizationName(inviteData.organizationName || "Unknown Organization");

      // Persist invitation data for onboarding flow continuity
      try {
        const invitationDetails = {
          inviteCode: inviteCode.trim(),
          teamId: inviteData.teamId ?? "",
          teamName: inviteData.teamName || inviteData.organizationName || "Unknown Team",
          organizationId: inviteData.organizationId ?? null,
          organizationName: inviteData.organizationName ?? null,
          userId: currentUserId ?? undefined,
          timestamp: Date.now(),
        };

        if (typeof window !== "undefined") {
          const serialized = JSON.stringify(invitationDetails);
          window.localStorage.setItem("invitationData", serialized);
          window.sessionStorage.setItem("invitationData", serialized);
          window.dispatchEvent(
            new CustomEvent("invitationDataUpdated", { detail: invitationDetails })
          );
        }
      } catch (storageError) {
        console.warn("Failed to persist invitation data:", storageError);
      }

      toast.success(`Invite code accepted! Joining ${inviteData.organizationName || "organization"}`);
    } catch (error: any) {
      console.error("Exception validating invite code:", error);
      toast.error(
        `Failed to validate invitation: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!newOrgName.trim()) {
      toast.error("Please enter an organization name");
      return;
    }

    setIsLoading(true);
    try {
      // Create the organization in database
      // Note: apiService.post returns the data directly (unwrapped from response.data.data)
      const newOrg = await organizationsService.createOrganization({
        name: newOrgName.trim(),
        description: `Organization created by ${userEmail}`,
      });

      if (!newOrg || !newOrg.id) {
        console.error('Error creating organization: Invalid response');
        toast.error('Failed to create organization. Please try again.');
        return;
      }

      // Backend automatically adds creator as admin member
      setSelectedOrganizationId(newOrg.id);
      setSelectedOrganizationName(newOrg.name);
      toast.success(`Organization "${newOrg.name}" created successfully!`);
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error('Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold">Join or Create Organization</h3>
        <p className="text-sm text-muted-foreground">
          Organizations help you collaborate with your colleagues and track team progress.
        </p>
      </div>

      <RadioGroup
        value={selectionMode}
        onValueChange={(value) => setSelectionMode(value as typeof selectionMode)}
        className="space-y-4"
      >
        {/* Domain-based organizations */}
        {(domainOrganizations.length > 0 || emailDomain) && (
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="domain" id="domain" />
              <Label htmlFor="domain" className="font-medium">Join by email domain</Label>
            </div>

            {selectionMode === "domain" && (
              <div className="ml-6 space-y-3">
                {isLoading ? (
                  <div className="p-4 border rounded-md">
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                      <span className="text-sm">Looking for organizations...</span>
                    </div>
                  </div>
                ) : domainOrganizations.length > 0 ? (
                  domainOrganizations.map((org) => (
                    <div
                      key={org.id}
                      className={`p-4 border rounded-md cursor-pointer hover:bg-muted/50 ${selectedOrganizationId === org.id ? "border-primary bg-primary/5" : ""
                        }`}
                      onClick={() => {
                        setSelectedOrganizationId(org.id);
                        setSelectedOrganizationName(org.name);
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{org.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {org.domain && `${org.domain} • `}{org.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 border rounded-md text-center text-muted-foreground">
                    <Mail className="h-8 w-8 mx-auto mb-2" />
                    <p className="text-sm">No organizations found for {emailDomain}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Invite code */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="invite" id="invite" />
            <Label htmlFor="invite" className="font-medium">Join with invite code</Label>
          </div>

          {selectionMode === "invite" && (
            <div className="ml-6 space-y-3">
              <div className="flex space-x-2">
                <Input
                  placeholder="Enter invite code (e.g., DEMO123)"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={handleInviteCodeValidation}
                  disabled={isLoading || !inviteCode.trim()}
                >
                  Validate
                </Button>
              </div>
              {selectedOrganizationId && selectedOrganizationName && selectionMode === "invite" && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">Joining: {selectedOrganizationName}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Create new organization */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="create" id="create" />
            <Label htmlFor="create" className="font-medium">Create new organization</Label>
          </div>

          {selectionMode === "create" && (
            <div className="ml-6 space-y-3">
              <div className="flex space-x-2">
                <Input
                  placeholder="Organization name (e.g., Acme Corp)"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  className="flex-1"
                />
                <Button
                  onClick={handleCreateOrganization}
                  disabled={!newOrgName.trim()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Create
                </Button>
              </div>
              {selectedOrganizationId && selectedOrganizationName && selectionMode === "create" && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <div className="flex items-center space-x-2">
                    <Building2 className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium">Creating: {selectedOrganizationName}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </RadioGroup>

      {selectedOrganizationId && selectedOrganizationName && (
        <div className="p-4 bg-muted rounded-md">
          <div className="text-sm">
            <strong>Selected:</strong> {selectedOrganizationName}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            You'll be able to collaborate with colleagues and access team features.
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSelectionStep;